import React, { useEffect } from 'react';
import { useLoginContext } from '../../../../../contexts/LoginContext';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EventType from './EventType';
import SpecialEventType from './SpecialEventType';
import InfoCard from './InfoCard';
import { StepperComponentProps } from '../../../NewAccountStepper/types';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useBaseStepperContext } from '../../contexts/BaseStepperContext';
import { stepperStyles } from '../../styles';

const eventTypeDescriptions = [
  'Incidents are accidents, traffic signal outages, and unscheduledroadway blockages.',
  'Congestion events are traffic slowdowns.',
  'Roadwork events are scheduled road construction and other scheduled roadway work.',
  'Special alerts include weather warnings and road closure alerts.',
];

const eventLevelDescriptions = [
  'None: You will not receive alerts for this event type',
  'Low: No known delays',
  'Medium: Moderate delays',
  'High: Heavy delays',
  'Closed: One or both directions of travel closed',
];

const types = [
  {
    alerttype: 'incident',
    name: 'Incident',
    description:
      'accidents, traffic signal outages, and other unscheduled roadway events',
  },
  {
    alerttype: 'congestion',
    eventtypes: 'mainline_congestion,ramp_congestion',
    name: 'Congestion',
    description: 'traffic slow downs',
  },
  {
    alerttype: 'roadwork',
    name: 'Roadwork',
    description:
      'scheduled road construction and other maintenance that affects roadways',
  },
];

const EventTypeComponent: React.FC<StepperComponentProps> = ({
  description,
  stepIndex,
}) => {
  const { user, selectedProfileIndex, setError } = useLoginContext();

  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];

  const { updateStepValidation } = useBaseStepperContext();

  // Use stepIndex prop or default to 2 for backward compatibility
  const validationStepIndex = stepIndex !== undefined ? stepIndex : 2;

  const getMissingFields = React.useCallback(() => {
    if (
      !selectedProfile ||
      (selectedProfile.allImpactEventTypes.length === 0 &&
        Object.keys(selectedProfile.eventTypeImpacts).length === 0 &&
        selectedProfile.regionlessEventTypes.length === 0)
    ) {
      return ['At least one event type selection'];
    }
    return [];
  }, [selectedProfile]);

  const missingFields = React.useMemo(
    () => getMissingFields(),
    [getMissingFields]
  );
  const hasErrors = React.useMemo(
    () => missingFields.length > 0,
    [missingFields]
  );

  const warningMessage = React.useMemo(() => {
    if (missingFields.length === 0) return '';
    return 'Please select at least one event type to receive alerts';
  }, [missingFields]);

  // Update step validation whenever validation state changes
  useEffect(() => {
    updateStepValidation(validationStepIndex, warningMessage, hasErrors);
  }, [updateStepValidation, hasErrors, warningMessage, validationStepIndex]);

  useEffect(() => {
    if (
      !selectedProfile ||
      (selectedProfile.allImpactEventTypes.length === 0 &&
        Object.keys(selectedProfile.eventTypeImpacts).length === 0 &&
        selectedProfile.regionlessEventTypes.length === 0)
    ) {
      setError(true);
      return;
    }
    setError(false);
  }, [setError, selectedProfile]);

  const theme = useTheme();

  return (
    <Box sx={stepperStyles.pageContainer}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={stepperStyles.pageTitle}
        >
          Configure Event Notifications
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription}>
          {description}
        </Typography>
      </Box>

      {/* Configuration Section */}
      <InfoCard
        title="Select Your Preferences"
        elevation={1}
        showDivider={false}
        icon={TaskAltIcon}
      >
        {/* Event Type Selection */}
        <Box sx={stepperStyles.eventContainer}>
          <SpecialEventType
            included={selectedProfile?.regionlessEventTypes.includes(
              'Special Alert'
            )}
          />
          {types.map((type, index) => {
            const impactLevelType =
              selectedProfile?.eventTypeImpacts[type.name];
            const allImpacts = selectedProfile?.allImpactEventTypes.includes(
              type.name
            );
            return (
              <EventType
                key={type.name}
                type={type}
                included={
                  selectedProfile?.eventTypeImpacts[type.name] !== undefined ||
                  allImpacts
                }
                impactLevel={allImpacts ? 'All' : impactLevelType}
                isFirst={index === 0}
              />
            );
          })}
        </Box>
      </InfoCard>

      {/* Information Cards Side by Side */}
      <Box sx={stepperStyles.eventTypeList}>
        <InfoCard
          title="Event Types"
          icon={CarCrashIcon}
          items={eventTypeDescriptions}
        />

        <InfoCard
          title="Impact Levels"
          icon={SettingsIcon}
          description="The selected Impact Level is the lowest impact level for which you want to receive alerts. For example, if you chose Medium, you will receive alerts for events at impact levels Medium, High, and Closed."
          items={eventLevelDescriptions}
        />
      </Box>
    </Box>
  );
};

export default EventTypeComponent;
